import * as React from 'react';
import {
  Box, Container, Typography, Paper, Button, Chip,
  Dialog, DialogTitle, DialogContent, DialogActions,
  Rating, TextField, Stack, CircularProgress, Snackbar, Alert, Divider, IconButton
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import StarIcon from '@mui/icons-material/Star';
import StarHalfIcon from '@mui/icons-material/StarHalf';
import RateReviewIcon from '@mui/icons-material/RateReview';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useNavigate, useLocation } from 'react-router-dom';
import { getAccess } from '../../lib/auth';

// i18n
import { useI18n } from '../../i18n/I18nProvider';
import avisI18n from '../../i18n/components/services/avis.i18n';

/* ========= Thème bleu ========= */
const BLUE = '#0B5FFF';
const BLUE_HOVER = '#0846C6';
const BLUE_BG = 'rgba(11,95,255,0.08)';
const BLUE_BORDER = '#E3ECFF';

/* ========= Base API : 100% depuis .env (aucun fallback en dur) ========= */
const API = (() => {
  const raw =
    (import.meta?.env && (import.meta.env.VITE_API_URL || import.meta.env.VITE_BACKEND_URL)) ||
    process.env.REACT_APP_API_URL ||
    process.env.API_URL ||
    '';

  const base = String(raw).trim().replace(/\/+$/, '');
  if (!base) {
    // On fail vite pour éviter d'appeler un endpoint vide par erreur.
    throw new Error(
      'API base URL manquante. Définis VITE_API_URL (Vite) ou REACT_APP_API_URL (CRA) dans ton .env.'
    );
  }
  return base; // ⚠️ on n’ajoute PAS /api — tu le mets tel quel dans le .env
})();

/* ========= Utils ========= */
const fmtRating = (r) => Number(r || 0).toFixed(1).replace('.', ',');

/* ========= Hook: charge 3 avis approuvés ========= */
function useApprovedReviews({ pageSize = 3 }) {
  const { t } = useI18n();
  const [initial, setInitial] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState('');
  const [stats, setStats] = React.useState({ count: 0, avg: 0 });

  React.useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        setLoading(true);
        setError('');
        const url = `${API}/reviews?approved=true&limit=${pageSize}&offset=0&order=createdAt:DESC`;
        const r = await fetch(url);
        if (!r.ok) throw new Error('network');
        const data = await r.json();
        if (!mounted) return;

        const rows = Array.isArray(data?.rows) ? data.rows : [];
        setInitial(rows);

        const count = Number(data?.count || rows.length || 0);
        const avg =
          rows.length > 0
            ? Math.round(
                (rows.reduce((s, it) => s + Number(it.rating || 0), 0) / rows.length) * 20
              ) / 20
            : 0;

        setStats({ count, avg });
      } catch {
        if (mounted) setError(t('services.avis.errors.loadMain'));
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [pageSize, t]);

  return { initial, loading, error, stats };
}

/* ========= Carte avis (compacte, sans date) ========= */
function ReviewCard({ review, dense = false }) {
  return (
    <Paper
      elevation={0}
      sx={{
        p: dense ? 1.25 : 1.75,
        borderRadius: 2,
        display: 'flex',
        flexDirection: 'column',
        gap: dense ? 0.6 : 0.8,
        border: `1px solid ${BLUE_BORDER}`,
        backgroundColor: '#fff',
      }}
    >
      <Stack direction="row" alignItems="center" spacing={0.75}>
        <Rating
          name="read-only"
          value={Number(review.rating || 0)}
          precision={0.5}
          readOnly
          icon={<StarIcon fontSize="small" sx={{ color: '#FFC107' }} />}
          emptyIcon={<StarIcon fontSize="small" sx={{ color: 'rgba(0,0,0,0.2)' }} />}
          sx={{ mr: 0.25 }}
        />
        <Chip
          size="small"
          label={fmtRating(review.rating)}
          sx={{ bgcolor: BLUE_BG, color: BLUE, fontWeight: 700, height: dense ? 20 : 22 }}
        />
      </Stack>

      <Typography sx={{ fontWeight: 800, fontSize: dense ? 13.5 : 14.5, mt: 0.25 }}>
        {review.companyName}
      </Typography>

      <Typography sx={{ color: 'text.secondary', lineHeight: 1.5, fontSize: dense ? 13 : 14 }}>
        {review.content}
      </Typography>
    </Paper>
  );
}

/* ========= Section principale ========= */
export default function Avis() {
  const { t, register } = useI18n();
  React.useEffect(() => { register('services', avisI18n); }, [register]);

  const { initial, loading, error, stats } = useApprovedReviews({ pageSize: 3 });
  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [prefetching, setPrefetching] = React.useState(false);
  const [prefetched, setPrefetched] = React.useState({ rows: [], count: 0, offset: 0 });

  const preOpenDialog = async () => {
    setPrefetching(true);
    try {
      const PAGE = 12;
      const url = `${API}/reviews?approved=true&limit=${PAGE}&offset=0&order=createdAt:DESC`;
      const r = await fetch(url);
      if (!r.ok) throw new Error();
      const data = await r.json();
      const rows = Array.isArray(data?.rows) ? data.rows : [];
      setPrefetched({ rows, count: Number(data?.count || rows.length || 0), offset: rows.length });
      setDialogOpen(true); // ✅ on ouvre seulement une fois le 1er lot chargé
    } catch {
      // on reste sur la section, pas d'ouverture de popup
    } finally {
      setPrefetching(false);
    }
  };

  return (
    <Box sx={{ bgcolor: '#fff' }}>
      <Container maxWidth="lg" sx={{ pt: { xs: 3, md: 2 }, pb: { xs: 4, md: 6 } }}>
        <Typography
          component="h2"
          sx={{
            fontWeight: 800,
            fontSize: { xs: 22, sm: 24, md: 26 },
            lineHeight: 1.15,
            mb: { xs: 1.25, md: 1.75 },
            color: BLUE,
          }}
        >
          {t('services.avis.title')}
        </Typography>

        {/* Bandeau stats (bleu) */}
        <Paper
          variant="outlined"
          sx={{
            mb: { xs: 1.75, md: 2 },
            p: 1.5,
            borderRadius: 2,
            borderColor: BLUE_BORDER,
            background: BLUE_BG,
            display: 'flex',
            alignItems: { xs: 'flex-start', sm: 'center' },
            gap: 1.25,
            flexWrap: 'wrap',
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1}>
            <Rating
              value={stats.avg}
              readOnly
              precision={0.5}
              icon={<StarIcon sx={{ color: '#FFC107' }} />}
              emptyIcon={<StarIcon sx={{ color: 'rgba(0,0,0,0.2)' }} />}
            />
            <Typography sx={{ fontWeight: 800, color: BLUE }}>
              {stats.avg ? stats.avg.toFixed(1).replace('.', ',') : '—'}/5
            </Typography>
          </Stack>
          <Divider flexItem orientation="vertical" sx={{ borderColor: BLUE_BORDER }} />
          <Typography sx={{ color: BLUE }}>
            {stats.count
              ? `${stats.count} ${t('services.avis.stats.countSuffix')}`
              : t('services.avis.stats.none')}
          </Typography>
        </Paper>

        {/* Aperçu (3 max) */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress size={22} />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ my: 1 }}>{error}</Alert>
        ) : initial.length === 0 ? (
          <Alert severity="info" sx={{ my: 1 }}>{t('services.avis.stats.none')}</Alert>
        ) : (
          <Stack spacing={1.75}>
            {initial.slice(0, 3).map((r) => (
              <ReviewCard key={r.id} review={r} />
            ))}
          </Stack>
        )}

        {/* Actions */}
        <Stack direction="row" justifyContent="center" sx={{ mt: { xs: 2.25, md: 3 } }}>
          <Button
            variant="contained"
            endIcon={!prefetching ? <ExpandMoreIcon /> : null}
            onClick={preOpenDialog}
            disabled={prefetching}
            sx={{
              textTransform: 'none',
              fontWeight: 700,
              borderRadius: 99,
              py: 0.6,
              px: 2.2,
              backgroundColor: BLUE,
              '&:hover': { backgroundColor: BLUE_HOVER },
              minWidth: 220,
            }}
          >
            {prefetching ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : t('services.avis.btn.viewMore')}
          </Button>
        </Stack>

        {/* Popup liste + laisser un avis */}
        <ReviewsDialog
          open={dialogOpen}
          onClose={() => setDialogOpen(false)}
          initialRows={prefetched.rows}
          initialCount={prefetched.count}
          initialOffset={prefetched.offset}
        />
      </Container>
    </Box>
  );
}

/* ========= Dialog (liste verticale, dense) ========= */
function ReviewsDialog({ open, onClose, initialRows, initialCount, initialOffset }) {
  const { t } = useI18n();
  const [rows, setRows] = React.useState([]);
  const [count, setCount] = React.useState(0);
  const [loading, setLoading] = React.useState(false);
  const [offset, setOffset] = React.useState(0);
  const [snack, setSnack] = React.useState({ open: false, msg: '', sev: 'success' });

  React.useEffect(() => {
    if (!open) return;
    setRows(initialRows || []);
    setCount(Number(initialCount || 0));
    setOffset(Number(initialOffset || 0));
  }, [open, initialRows, initialCount, initialOffset]);

  const PAGE = 12;
  const canLoadMore = offset < count;

  const loadMore = async () => {
    if (loading || !canLoadMore) return;
    setLoading(true);
    try {
      const url = `${API}/reviews?approved=true&limit=${PAGE}&offset=${offset}&order=createdAt:DESC`;
      const r = await fetch(url);
      if (!r.ok) throw new Error('load fail');
      const data = await r.json();
      const batch = Array.isArray(data?.rows) ? data.rows : [];
      setRows(prev => [...prev, ...batch]);
      setOffset(prev => prev + batch.length);
    } catch {
      setSnack({ open: true, msg: t('services.avis.errors.loadList'), sev: 'error' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      fullWidth
      maxWidth="md"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          borderRadius: 3,
          overflow: 'hidden',
          boxShadow: '0 12px 36px rgba(0,0,0,.18)',
        },
      }}
    >
      <DialogTitle
        sx={{
          pr: 1,
          pl: 2,
          py: 1,
          fontWeight: 800,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: `1px solid ${BLUE_BORDER}`,
          color: BLUE,
        }}
      >
        {t('services.avis.dialog.title')}
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent
        dividers
        sx={{
          pt: 1,
          pb: 1,
          px: { xs: 1.25, sm: 1.75 },
          maxHeight: '70vh',
          overflowY: 'auto',
          backgroundColor: '#fafafa',
        }}
      >
        {/* Header compact: compteur + CTA */}
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          sx={{ mb: 1 }}
        >
          <Typography sx={{ color: BLUE, fontSize: 14 }}>
            {count ? `${Math.min(offset, count)}/${count} ${t('services.avis.stats.shownSuffix')}` : '—'}
          </Typography>

          <LeaveReviewButton
            onSuccess={() => {
              setSnack({
                open: true,
                msg: t('services.avis.leave.success'),
                sev: 'success',
              });
            }}
          />
        </Stack>

        {/* Liste verticale uniforme */}
        {rows.length === 0 ? (
          <Alert severity="info" sx={{ my: 1 }}>{t('services.avis.stats.none')}</Alert>
        ) : (
          <Stack spacing={1.25}>
            {rows.map((r) => (
              <ReviewCard key={r.id} review={r} dense />
            ))}
          </Stack>
        )}

        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 1.25 }}>
            <CircularProgress size={20} />
          </Box>
        )}

        {canLoadMore && !loading && (
          <Stack direction="row" justifyContent="center" sx={{ mt: 1 }}>
            <Button
              variant="outlined"
              onClick={loadMore}
              endIcon={<ExpandMoreIcon />}
              sx={{
                textTransform: 'none',
                fontWeight: 700,
                borderRadius: 2,
                px: 2,
                borderColor: BLUE,
                color: BLUE,
                '&:hover': { borderColor: BLUE_HOVER, backgroundColor: BLUE_BG },
              }}
            >
              {t('services.avis.btn.loadMore')}
            </Button>
          </Stack>
        )}
      </DialogContent>

      <DialogActions sx={{ py: 0.75, px: 1.5 }}>
        <Button onClick={onClose} sx={{ textTransform: 'none', color: BLUE }}>{t('services.avis.btn.close')}</Button>
      </DialogActions>

      <Snackbar
        open={snack.open}
        autoHideDuration={3000}
        onClose={() => setSnack(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnack(s => ({ ...s, open: false }))}
          severity={snack.sev}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snack.msg}
        </Alert>
      </Snackbar>
    </Dialog>
  );
}

/* ========= Bouton "laisser un avis" (pas de global location) ========= */
function LeaveReviewButton({ onSuccess }) {
  const { t } = useI18n();
  const token = getAccess();
  const isAuthenticated = Boolean(token);
  const [openForm, setOpenForm] = React.useState(false);

  const navigate = useNavigate();
  const loc = useLocation();
  const redirect = `${loc.pathname}${loc.search}${loc.hash}`;

  if (!isAuthenticated) {
    return (
      <Button
        variant="contained"
        startIcon={<RateReviewIcon />}
        onClick={() => navigate(`/login?redirect=${encodeURIComponent(redirect)}`)}
        sx={{
          textTransform: 'none',
          fontWeight: 700,
          borderRadius: 2,
          backgroundColor: BLUE,
          '&:hover': { backgroundColor: BLUE_HOVER },
        }}
      >
        {t('services.avis.btn.leave')}
      </Button>
    );
  }

  return (
    <>
      <Button
        variant="contained"
        startIcon={<RateReviewIcon />}
        onClick={() => setOpenForm(true)}
        sx={{
          textTransform: 'none',
          fontWeight: 700,
          borderRadius: 2,
          backgroundColor: BLUE,
          '&:hover': { backgroundColor: BLUE_HOVER },
        }}
      >
        {t('services.avis.btn.leave')}
      </Button>
      <LeaveReviewForm
        open={openForm}
        onClose={() => setOpenForm(false)}
        token={token}
        onSuccess={() => {
          setOpenForm(false);
          onSuccess?.();
        }}
      />
    </>
  );
}

/* ========= Formulaire d’envoi d’avis ========= */
function LeaveReviewForm({ open, onClose, token, onSuccess }) {
  const { t } = useI18n();
  const [rating, setRating] = React.useState(4.5);
  const [content, setContent] = React.useState('');
  const [loading, setLoading] = React.useState(false);
  const [err, setErr] = React.useState('');

  const submit = async (e) => {
    e?.preventDefault?.();
    try {
      setLoading(true);
      setErr('');
      const r = await fetch(`${API}/reviews`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ rating, content }),
      });
      if (!r.ok) {
        const m = await r.json().catch(() => ({}));
        throw new Error((m && m.message) || t('services.avis.errors.submit'));
      }
      onSuccess?.();
    } catch (e) {
      setErr(e.message || t('services.avis.errors.network'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle
        sx={{
          pr: 1,
          pl: 2,
          py: 1,
          fontWeight: 800,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: `1px solid ${BLUE_BORDER}`,
          color: BLUE,
        }}
      >
        {t('services.avis.leave.title')}
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ pt: 1.25 }}>
        <Box component="form" onSubmit={submit}>
          <Typography sx={{ mb: 0.5, fontWeight: 700, color: BLUE }}>{t('services.avis.leave.yourRating')}</Typography>
          <Rating
            value={rating}
            precision={0.5}
            onChange={(_, v) => setRating(v || 0)}
            icon={<StarIcon sx={{ color: '#FFC107' }} />}
            emptyIcon={<StarIcon sx={{ color: 'rgba(0,0,0,0.2)' }} />}
            sx={{ mb: 1.5 }}
          />
          <Typography sx={{ mb: 0.5, fontWeight: 700, color: BLUE }}>{t('services.avis.leave.yourReview')}</Typography>
          <TextField
            value={content}
            onChange={(e) => setContent(e.target.value)}
            placeholder={t('services.avis.leave.placeholder')}
            fullWidth
            multiline
            minRows={4}
            inputProps={{ maxLength: 2000 }}
          />
          {err && <Alert sx={{ mt: 1.25 }} severity="error">{err}</Alert>}
        </Box>
      </DialogContent>
      <DialogActions sx={{ py: 0.75, px: 1.5 }}>
        <Button onClick={onClose} sx={{ textTransform: 'none', color: BLUE }}>{t('services.avis.btn.cancel')}</Button>
        <Button
          onClick={submit}
          variant="contained"
          disabled={loading || content.trim().length < 3 || rating < 1}
          startIcon={loading ? <CircularProgress size={16} /> : <StarHalfIcon />}
          sx={{
            textTransform: 'none',
            fontWeight: 700,
            borderRadius: 2,
            backgroundColor: BLUE,
            '&:hover': { backgroundColor: BLUE_HOVER },
          }}
        >
          {t('services.avis.btn.send')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}